<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import TablesAPI from 'dashboard/api/tables';

// One editor per column type. Every type in the backend's COLUMN_TYPES has a
// branch here; anything unrecognised falls through to a plain text input
// rather than becoming uneditable.
const props = defineProps({
  column: { type: Object, required: true },
  modelValue: { type: [String, Number, Boolean, Array, null], default: null },
  disabled: { type: Boolean, default: false },
  // Relation columns only: { id, label } resolved by the server for the
  // current value.
  relation: { type: Object, default: null },
});

const emit = defineEmits(['update:modelValue']);

const inputType = computed(() => {
  switch (props.column.column_type) {
    case 'number':
    case 'currency':
    case 'percent':
      return 'number';
    case 'date':
      return 'date';
    case 'datetime':
      return 'datetime-local';
    case 'email':
      return 'email';
    case 'phone':
      return 'tel';
    case 'link':
      return 'url';
    default:
      return 'text';
  }
});

const isSelect = computed(() => props.column.column_type === 'list');
const isMultiSelect = computed(
  () => props.column.column_type === 'multi_select'
);
const isCheckbox = computed(() => props.column.column_type === 'checkbox');
const isLongText = computed(() => props.column.column_type === 'long_text');
const options = computed(() => props.column.config?.options || []);

const selectedMulti = computed(() =>
  Array.isArray(props.modelValue) ? props.modelValue : []
);

const onInput = value => emit('update:modelValue', value);

const toggleMulti = option => {
  const current = selectedMulti.value;
  const next = current.includes(option)
    ? current.filter(item => item !== option)
    : [...current, option];
  emit('update:modelValue', next);
};

const { t } = useI18n();

// Relation: search the target table's rows server-side and store the id.
const isRelation = computed(() => props.column.column_type === 'relation');
const relationQuery = ref('');
const relationResults = ref([]);
const relationPicked = ref(null);
const relationLabel = computed(() => {
  if (props.modelValue === null || props.modelValue === '') return '';
  if (relationPicked.value?.id === Number(props.modelValue))
    return relationPicked.value.label;
  return props.relation?.label || `#${props.modelValue}`;
});

const labelFor = row =>
  row.data?.[props.column.config?.display_column_key] ?? `#${row.id}`;

const searchRelation = async () => {
  const { data } = await TablesAPI.getRows(
    props.column.config?.target_table_id,
    { search: relationQuery.value || undefined, per_page: 20 }
  );
  relationResults.value = (data.payload || []).map(row => ({
    id: row.id,
    label: labelFor(row),
  }));
};

const pickRelation = option => {
  relationPicked.value = option;
  relationResults.value = [];
  relationQuery.value = '';
  emit('update:modelValue', option ? option.id : null);
};

const baseClass =
  'w-full px-2 py-1 text-sm bg-transparent border rounded border-n-weak text-n-slate-12 disabled:opacity-60';
</script>

<template>
  <div v-if="isRelation" class="flex flex-col gap-1">
    <div class="flex items-center gap-2">
      <span class="text-sm text-n-slate-12">
        {{ relationLabel || t('TABLES.ROW.RELATION_NONE') }}
      </span>
      <button
        v-if="relationLabel && !disabled"
        type="button"
        class="text-xs text-n-ruby-11"
        @click="pickRelation(null)"
      >
        {{ t('TABLES.ROW.RELATION_CLEAR') }}
      </button>
    </div>
    <div v-if="!disabled" class="flex gap-1">
      <input
        v-model="relationQuery"
        type="text"
        :placeholder="t('TABLES.ROW.RELATION_SEARCH')"
        :class="baseClass"
        @keyup.enter="searchRelation"
      />
      <button
        type="button"
        class="px-2 text-xs border rounded border-n-weak text-n-slate-12"
        @click="searchRelation"
      >
        {{ t('TABLES.SEARCH_ACTION') }}
      </button>
    </div>
    <ul
      v-if="relationResults.length"
      class="m-0 overflow-y-auto list-none border rounded max-h-40 border-n-weak"
    >
      <li v-for="option in relationResults" :key="option.id">
        <button
          type="button"
          class="w-full px-2 py-1 text-sm text-left hover:bg-n-alpha-2 text-n-slate-12"
          @click="pickRelation(option)"
        >
          {{ option.label }}
        </button>
      </li>
    </ul>
  </div>

  <input
    v-else-if="isCheckbox"
    type="checkbox"
    :checked="!!modelValue"
    :disabled="disabled"
    @change="onInput($event.target.checked)"
  />

  <textarea
    v-else-if="isLongText"
    :value="modelValue"
    :disabled="disabled"
    rows="3"
    :class="baseClass"
    @input="onInput($event.target.value)"
  />

  <select
    v-else-if="isSelect"
    :value="modelValue"
    :disabled="disabled"
    :class="baseClass"
    @change="onInput($event.target.value)"
  >
    <option value="" />
    <option v-for="option in options" :key="option" :value="option">
      {{ option }}
    </option>
  </select>

  <div v-else-if="isMultiSelect" class="flex flex-wrap gap-1">
    <button
      v-for="option in options"
      :key="option"
      type="button"
      :disabled="disabled"
      class="px-2 py-0.5 text-xs border rounded border-n-weak"
      :class="
        selectedMulti.includes(option)
          ? 'bg-n-brand text-white'
          : 'text-n-slate-11'
      "
      @click="toggleMulti(option)"
    >
      {{ option }}
    </button>
  </div>

  <input
    v-else
    :type="inputType"
    :value="modelValue"
    :disabled="disabled"
    :min="column.config?.min"
    :max="column.config?.max"
    :class="baseClass"
    @input="onInput($event.target.value)"
  />
</template>
